import type { ImageSource } from "expo-image";
import { UnistylesRuntime } from "react-native-unistyles";

import useConfig from "$src/core/hooks/use-config";
import CustomPlatform from "$src/core/utils/platform/platform";

const normalizeUri = (rawUri: string | undefined): string | undefined => {
  if (!rawUri) return undefined;
  return rawUri.startsWith("//") ? `https:${rawUri}` : rawUri;
};

const getDimensionKey = (width?: number, height?: number): string => {
  if (width === undefined && height === undefined) return "";
  if (width === undefined) return `-${height}`;
  if (height === undefined) return `${width}-`;
  return `${width}-${height}`;
};

const buildCacheKey = (
  uri: string,
  options?: {
    scales: number[];
    dimensions?: ({ width?: number; height?: number } | undefined)[];
  },
): string => {
  let key = `${uri}`;
  if (options?.scales) {
    key += `|${options.scales.join(",")}`;
  }
  if (options?.dimensions) {
    for (const dimension of options.dimensions) {
      key += `|${dimension?.width ?? ""}x${dimension?.height ?? ""}`;
    }
  }
  return key;
};

// Module-level cache, so we're returning the exact same result pointer
// for identical inputs to avoid re-running expo-image source diffing
// (because the variable pointers changed after re-render).
const cache = new Map<string, ImageSource[]>();

// Safety measure to prevent cache size exhaustion (512 entries)
const MAX_CACHE_ENTRIES = 512;

const useResponsiveImage = (
  rawUri: string | undefined,
  options?: {
    dimensions?: ({ width?: number; height?: number } | undefined)[];
  },
): ImageSource[] | undefined => {
  const { cdn } = useConfig();

  const scales = CustomPlatform.select({
    native: [UnistylesRuntime.pixelRatio],
    tv: [UnistylesRuntime.pixelRatio],
    web: [1, UnistylesRuntime.pixelRatio],
    default: [1],
  });

  const uri = normalizeUri(rawUri);
  if (!uri) return undefined;

  const cacheKey = buildCacheKey(uri, { ...options, scales });

  const cached = cache.get(cacheKey);
  if (cached) return cached;

  let result: ImageSource[];

  if (uri.endsWith(".svg")) {
    result = [{ uri }];
  } else {
    const seen = new Set<string>();
    const sources: ImageSource[] = [];

    if (options?.dimensions) {
      for (const dimension of options.dimensions) {
        for (const scale of scales) {
          const scaledWidth = dimension?.width
            ? Math.round(dimension.width * scale)
            : undefined;
          const scaledHeight = dimension?.height
            ? Math.round(dimension.height * scale)
            : undefined;

          const dimensionKey = getDimensionKey(scaledWidth, scaledHeight);

          if (seen.has(dimensionKey)) continue;
          seen.add(dimensionKey);

          sources.push({
            uri: cdn.image.buildResponsiveUri(uri, {
              width: scaledWidth,
              height: scaledHeight,
            }),
            width: scaledWidth,
            height: scaledHeight,
          });
        }
      }
    }

    result = sources.length === 0 ? [{ uri }] : sources;
  }

  // Safety measure to prevent cache size exhaustion
  if (cache.size >= MAX_CACHE_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey !== undefined) cache.delete(oldestKey);
  }

  cache.set(cacheKey, result);

  return result;
};

export default useResponsiveImage;
